import React from "react";

import { toast } from "react-toastify";
import { isEnabled } from "utils/featureFlags";

import { push } from "connected-react-router";

import moment from "moment";

import * as ReportAPI from "api/ReportAPI";
import * as ProjectAPI from "api/ProjectAPI";

import * as reportReducer from "containers/Report/ReportReducer";
import * as projectReducer from "containers/Project/reducer";
import * as nextReducer from "containers/Next/NextReducer";
import * as monitoringReducer from "containers/Monitoring/reducer";
import * as setupReducer from "containers/Setup/reducer";

const DEFAULT_DATA_DRIFT_MONITORING_THRESHOLD = 10;
const DEFAULT_MODEL_DRIFT_MONITORING_THRESHOLD = 10;

const UPDATE_BASE = "nextMonitoring/UPDATE_BASE";
const UPDATE_FIELD = "nextMonitoring/UPDATE_FIELD";
const UPDATE_FIELD_VALUE = "nextMonitoring/UPDATE_FIELD_VALUE";

const DEFAULT_MONITORING = {};

const initialState = {};

function nextMonitoringReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_BASE:
      return {
        ...state,
        ...action.data
      };
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: action.data
      };
    case UPDATE_FIELD_VALUE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    default:
      return state;
  }
}

export const USER_GROUP_DATA_KEYS = ["label", "count", "profit", "count_cumulative", "profit_cumulative"];

/* Selectors */
export function getBase(state) {
  return state.nextMonitoring;
}

export function getNextMonitoring(state, id) {
  return getBase(state)?.[id];
}

export function getMonitoringSummary(state, id) {
  return getNextMonitoring(state, id)?.monitoringSummary;
}

export function getDataDriftWarningThreshold(state, id) {
  const dataDriftWarningThreshold = getNextMonitoring(state, id)?.dataDriftWarningThreshold;
  return dataDriftWarningThreshold === undefined ? DEFAULT_DATA_DRIFT_MONITORING_THRESHOLD : dataDriftWarningThreshold;
}

export function getModelDriftWarningThreshold(state, id) {
  const modelDriftWarningThreshold = getNextMonitoring(state, id)?.modelDriftWarningThreshold;

  return modelDriftWarningThreshold === undefined
    ? DEFAULT_DATA_DRIFT_MONITORING_THRESHOLD
    : modelDriftWarningThreshold;
}

export function getModelDriftWarningTargets(state, id) {
  return getNextMonitoring(state, id)?.modelDriftWarningTargets;
}

export function getUserTargeting(state, id) {
  return getNextMonitoring(state, id)?.userTargeting;
}

export function getUserGroup(state, id, index) {
  return getUserTargeting(state, id)?.[index];
}

function getGroupUserData(segmentId, userData, userIds, label = "") {
  const monitorLength = userIds.reduce((acc, userId) => {
    const currSegmentData = userData[userId]?.data[segmentId]?.data;
    const currDataLength = currSegmentData ? currSegmentData.length : 0;

    return Math.max(acc, currDataLength);
  }, 0);

  const cumulative = {
    count: 0,
    profit: 0
  };

  const retData = [];
  for (let monitorIndex = 0; monitorIndex < monitorLength; monitorIndex++) {
    const currData = {
      count: 0,
      profit: 0,
      count_cumulative: 0,
      profit_cumulative: 0,
      label: label
    };
    userIds.forEach((userId) => {
      const currEntry = userData[userId]?.data[segmentId]?.data?.[monitorIndex];
      if (!currEntry) {
        return;
      }
      if (!currData.date) {
        currData.date = currEntry.date;
      }

      const currTotal = currEntry.count + currData.count;
      const currProfit = currEntry.profit + currData.profit;

      currData.count = currTotal;
      currData.profit = currProfit;
    });

    cumulative.count = cumulative.count + currData.count;
    cumulative.profit = cumulative.profit + currData.profit;

    currData.count_cumulative = cumulative.count;
    currData.profit_cumulative = cumulative.profit;

    retData.push(currData);
  }

  return retData;
}

export function getUserTargetingPlotKey(index, dataKey) {
  return `group_${index}_${dataKey}`;
}

export function getGroupColor(index) {
  const colors = ["#512F77", "#F47C20"];

  const colorIndex = index % colors.length;

  return colors[colorIndex];
}

export function getUserTargetingPlot(state, id) {
  const projectId = nextReducer.getProjectId(state, id);

  const userData = monitoringReducer.getUserData(state, projectId);
  const segmentId = projectReducer.getSegmentId(state, projectId);

  const userTargeting = getUserTargeting(state, id) || [];

  let maxLength = 0;

  const groupData = userTargeting.map((userGroup, index) => {
    const groupName = userGroup?.label;
    const groupUsers = userGroup?.users;

    if (!groupUsers?.length) {
      return [];
    }

    const groupData = getGroupUserData(segmentId, userData, groupUsers, groupName);

    maxLength = Math.max(maxLength, groupData?.length);

    return groupData;
  });

  const retData = [];

  for (let i = 0; i < maxLength; i++) {
    const retDataPoint = {};
    groupData.forEach((userGroupData, index) => {
      const currData = userGroupData[i] || {};

      if (retDataPoint.date === undefined) {
        retDataPoint.date = currData.date;
      }

      USER_GROUP_DATA_KEYS.forEach((cKey) => {
        const fullKey = getUserTargetingPlotKey(index, cKey);
        retDataPoint[fullKey] = currData[cKey];
      });
    });
    retData.push(retDataPoint);
  }

  return retData;
}

/* Actions */
export function updateBase(data) {
  return {
    type: UPDATE_BASE,
    data: data
  };
}

export function updateNextMonitoring(id, data) {
  return {
    type: UPDATE_FIELD,
    id: id,
    data: data
  };
}

export function updateMonitoringSummary(id, monitoringSummary) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: monitoringSummary,
    key: "monitoringSummary"
  };
}

export function updateDataDriftWarningThreshold(id, threshold) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: threshold,
    key: "dataDriftWarningThreshold"
  };
}

export function updateModelDriftWarningThreshold(id, threshold) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: threshold,
    key: "modelDriftWarningThreshold"
  };
}

export function updateModelDriftWarningTargets(id, targets) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: targets,
    key: "modelDriftWarningTargets"
  };
}

export function updateUserTargeting(id, userTargeting) {
  return {
    type: UPDATE_FIELD_VALUE,
    id: id,
    data: userTargeting,
    key: "userTargeting"
  };
}

export function updateUserGroupByIndex(id, index, userGroup) {
  return (dispatch, getState) => {
    const userTargeting = getUserTargeting(getState(), id) || [];
    userTargeting[index] = userGroup;
    dispatch(updateUserTargeting(id, userTargeting));
  };
}

export function updateUserGroupValue(id, index, key, value) {
  return (dispatch, getState) => {
    const userGroup = getUserGroup(getState(), id, index) || {};
    userGroup[key] = value;
    dispatch(updateUserGroupByIndex(id, index, userGroup));
  };
}

/* Methods */
export function init(id) {
  return (dispatch, getState) => {
    const currMonitoring = getNextMonitoring(getState, id);
    if (!currMonitoring) {
      dispatch(updateNextMonitoring(id, { ...DEFAULT_MONITORING }));
    }
  };
}

export function updateReportMonitoringField(id, key, value, metadataOverride) {
  return (dispatch, getState) => {
    const getCurrentReport = () => {
      if (metadataOverride) {
        return Promise.resolve({ metadata: { ...metadataOverride } });
      }

      return ReportAPI.getReport(id);
    };

    return getCurrentReport().then((response) => {
      const currReport = response?.metadata;

      // This is an escape so we don't explode reports.
      // But that means monitoring can't be the first setting that gets saved
      // Probably not going to be an issue (but it is a danger)
      if (!currReport) {
        return;
      }

      currReport.monitoring = {
        ...currReport.monitoring,
        [key]: value
      };

      return ReportAPI.updateReport(id, currReport).then((r) => {
        const currRawReport = { ...reportReducer.getReport(getState(), id) };

        currRawReport.metadata.monitoring = {
          ...currRawReport.metadata.monitoring,
          [key]: value
        };

        dispatch(reportReducer.updateReportR(id, currRawReport));

        return value;
      });
    });
  };
}

export function checkMonitoringSummary(id, currNextOverride) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);

    const newSummary = monitoringReducer.getMonitoringSummary(getState(), projectId);

    const currSummary = currNextOverride?.monitoring?.monitoringSummary
      ? currNextOverride?.monitoring?.monitoringSummary
      : getMonitoringSummary(getState(), id);

    const hasNewSummary =
      newSummary !== undefined && Object.keys(newSummary)?.length > 0 && newSummary.data?.length > 0;
    if (!hasNewSummary) {
      return Promise.resolve();
    }

    const hasCurrentSummary =
      currSummary !== undefined && Object.keys(currSummary)?.length > 0 && currSummary.data?.length > 0;
    // Do not override current monitoring results with demo data
    // if (isEnabled("monitoring-demo") && hasCurrentSummary) {
    //   return Promise.resolve();
    // }

    const isMoreRecent = !hasCurrentSummary || moment(newSummary?.latestDate).isAfter(moment(currSummary?.latestDate));
    const isSameDate = moment(newSummary?.latestDate).isSame(moment(currSummary?.latestDate));
    const hasChangedSettings =
      currSummary?.isCumulative !== newSummary?.isCumulative ||
      currSummary?.label !== newSummary?.label ||
      currSummary?.unitLabel !== newSummary?.unitLabel;

    if (!hasCurrentSummary || isMoreRecent || (isSameDate && hasChangedSettings)) {
      return dispatch(updateReportMonitoringField(id, "monitoringSummary", newSummary, currNextOverride)).then(() => {
        dispatch(updateMonitoringSummary(id, newSummary));
      });
    }

    return Promise.resolve();
  };
}

export function deleteUserTargeting(id) {
  return (dispatch, getState) => {
    const currReport = reportReducer.getReport(getState(), id);

    const handleDelete = () => {
      dispatch(updateUserTargeting(id, undefined));
    };

    if (!currReport?.metadata?.monitoring?.userTargeting) {
      handleDelete();
      return Promise.resolve();
    }
    return dispatch(updateReportMonitoringField(id, "userTargeting", undefined)).then(() => {
      handleDelete();
      const projectId = nextReducer.getProjectId(getState(), id);
      dispatch(setupReducer.setFrontierPercentProcessed(projectId, [0, 1]));
      dispatch(projectReducer.handleUpdateMonitorSettings(projectId));
    });
  };
}

export function saveUserTargeting(id) {
  return (dispatch, getState) => {
    const projectId = nextReducer.getProjectId(getState(), id);
    const userTargeting = getUserTargeting(getState(), id);

    let shouldSave = false;
    userTargeting?.forEach((userGroup) => {
      const users = userGroup?.users;
      // const scenarioIndex = userGroup?.scenarioIndex;

      if (users?.length /* && scenarioIndex !== undefined */) {
        shouldSave = true;
      }
    });

    if (shouldSave) {
      // Disable User Settings
      dispatch(setupReducer.setFrontierPercentProcessed(projectId, [1, 0]));
      // dispatch(projectReducer.handleUpdateMonitorSettings(projectId))

      // Set User Levels
      const allUsers = [];
      userTargeting?.forEach((userGroup, index) => {
        userGroup?.users?.forEach((userId) => {
          if (!allUsers.includes(userId)) {
            allUsers.push(userId);
          }
        });
      });

      const segments = projectReducer.getSegments(getState(), projectId);

      const defaultUserRole = "CONTRIBUTOR";
      const userCreationPromises = [];

      false &&
        segments.forEach((seg) => {
          // if (segments?.length > 1 && !seg?.conditions?.length) {
          //   return;
          // }
          const segId = seg?.id;

          const existingUsers = {};

          seg?.roles?.forEach((cUser) => {
            existingUsers[cUser?.role_user?.id] = true;
          });

          allUsers.forEach((userId) => {
            if (!existingUsers[userId]) {
              const createUserPromise = ProjectAPI.addUserRoleToSegment(segId, userId, defaultUserRole);

              userCreationPromises.push(createUserPromise);
            }
          });
        });

      Promise.all([userCreationPromises]).then(() => {
        return dispatch(projectReducer.fetchProject(projectId)).then(() => {
          const segments = projectReducer.getSegments(getState(), projectId);

          const userPreferenceUpdatePromises = [];

          userTargeting?.forEach((userGroup, index) => {
            const users = userGroup?.users;

            if (!users?.length) {
              return;
            }

            const scenarioIndex =
              userGroup.scenarioIndex === undefined
                ? projectReducer.getDeployedScenarioIndex(getState(), projectId)
                : userGroup.scenarioIndex;
            const threshold = userGroup.scenarioIndex < 0 ? userGroup?.threshold : undefined;

            const selectedScenario = projectReducer.getScenarios(getState(), projectId)?.[scenarioIndex];

            segments.forEach((seg) => {
              if (segments?.length > 1 && !seg?.conditions?.length) {
                return;
              }

              const segId = seg?.id;

              let targetThreshold = threshold;
              if (targetThreshold === undefined) {
                const segScenario = Object.values(selectedScenario).find((e) => e?.project_segment_id === segId);
                const confusionMatrix = segScenario?.bestModel?.confusionMatrix ?? segScenario?.model?.confusionMatrix;
                targetThreshold =
                  (confusionMatrix.tp + confusionMatrix.fp) /
                  (confusionMatrix.tp + confusionMatrix.fp + confusionMatrix.tn + confusionMatrix.fn || 1);
              }

              if (isNaN(targetThreshold)) {
                return;
              }

              seg?.roles?.forEach((cRole) => {
                if (users?.includes(cRole?.role_user?.id)) {
                  const roleId = cRole.id;
                  const prefUpdatePromise = ProjectAPI.patchUserRoleSegmentPercent(roleId, threshold * 100);

                  userPreferenceUpdatePromises.push(prefUpdatePromise);
                }
              });
            });
          });

          return Promise.all(userPreferenceUpdatePromises).then(() => {
            return dispatch(projectReducer.fetchProject(projectId));
          });
        });
      });

      // Save Report
      return dispatch(updateReportMonitoringField(id, "userTargeting", userTargeting));
    }
    return Promise.resolve();
  };
}

export default nextMonitoringReducer;
